"use client"

import { useState } from "react"
import { IconCar, IconLoader2 } from "@tabler/icons-react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslations } from "next-intl"
import { toast } from "sonner"

import { AssignPopover, type AssignPopoverDriverOption } from "@/components/logistics/assign-popover"
import { Button } from "@/components/ui/button"
import { usePermissions } from "@/hooks/use-permissions"
import { ensureTravelLeg, getLogisticsBoard } from "@/lib/logistics-api"
import { cn } from "@/lib/utils"
import { useAuthStore } from "@/stores/auth-store"

import type { UnifiedRow } from "./use-needs-booking"

/**
 * Dentour 260923 — the Driver column: an arriving or departing patient's airport
 * ride, dispatched right from the desk board.
 *
 * Every trip already owns its `arrival_pickup` and `departure_dropoff` rides
 * (TransferSyncService derives them), so the job is putting a driver on them:
 *
 *  - no ride           → "Schedule drive" restores the trip's own leg (created
 *                        from the trip, or revived if it was cancelled);
 *  - ride, no driver   → "Assign driver" opens the logistics board's own assign
 *                        popover (driver + pickup time, one PATCH);
 *  - driver on it      → the driver and time, as a quiet chip (re-assignable).
 *
 * Dispatching needs `logistics.manage` on a clinic running logistics. Anyone
 * else sees the same state read-only, so the desk still knows whether the ride
 * is sorted without being offered a button that would 403.
 */
export function RideDriverAction({ row, onChanged }: { row: UnifiedRow; onChanged: () => void }) {
  const t = useTranslations("dashboard.frontdeskBooking.drive")
  const { can } = usePermissions()
  const logisticsOn = useAuthStore((s) => s.user?.feature_flags?.logistics_enabled ?? false)
  const canDispatch = logisticsOn && can("logistics.manage")
  const queryClient = useQueryClient()
  const [creating, setCreating] = useState(false)

  const ride = row.ride
  const drive = ride?.drive ?? null
  const rideDay = drive?.pickupDate ?? ride?.date ?? null

  // The day's roster with each driver's load, exactly as the logistics board
  // shows it — shared across rows on the same day (one request per day).
  const roster = useQuery({
    queryKey: ["logistics-board", rideDay],
    queryFn: () => getLogisticsBoard(rideDay as string),
    enabled: canDispatch && drive !== null && rideDay !== null,
    staleTime: 60_000,
  })
  const drivers: AssignPopoverDriverOption[] = (roster.data?.drivers ?? [])
    .filter((lane) => lane.driver.is_active)
    .map((lane) => ({
      driver: lane.driver,
      rideCount: lane.transfers.filter((transfer) => transfer.status !== "cancelled").length,
    }))

  if (!ride) return null

  const stop = (e: React.SyntheticEvent) => e.stopPropagation()

  if (drive === null) {
    if (!canDispatch || !ride.date) {
      return <span className="text-[10.5px] text-text-dim">{t("none")}</span>
    }
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 gap-1 px-2 text-[11px]"
        disabled={creating}
        onClick={async (e) => {
          e.stopPropagation()
          setCreating(true)
          try {
            // The trip's OWN leg (created, or revived if it was cancelled on
            // the logistics board) — so the next refresh finds it on the trip
            // and the button turns into "Assign driver" instead of offering to
            // add another loose ride. The server derives the pickup, including
            // the departure's check-in lead time.
            await ensureTravelLeg(ride.travelId, ride.kind)
            toast.success(t("scheduled"))
            onChanged()
          } catch {
            toast.error(t("failed"))
          } finally {
            setCreating(false)
          }
        }}
        onKeyDown={stop}
      >
        {creating ? <IconLoader2 className="size-3.5 animate-spin" /> : <IconCar className="size-3.5" />}
        {t("schedule")}
      </Button>
    )
  }

  const assigned = drive.driverId !== null
  const chip = (
    <span
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10.5px] font-medium",
        assigned
          ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
          : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
      )}
    >
      <IconCar className="size-3" />
      {assigned
        ? drive.pickupTime
          ? t("driverAt", { driver: drive.driverName ?? "—", time: drive.pickupTime })
          : (drive.driverName ?? "—")
        : t("noDriver")}
    </span>
  )

  if (!canDispatch) return chip

  return (
    <span onClick={stop} onKeyDown={stop}>
      <AssignPopover
        transfer={{ id: drive.transferId, pickup_time: drive.pickupTime, driver_id: drive.driverId }}
        drivers={drivers}
        onAssigned={() => {
          // The day's roster counts this ride now — every row on that day
          // shares the cached roster, so refresh it along with the board.
          void queryClient.invalidateQueries({ queryKey: ["logistics-board", rideDay] })
          onChanged()
        }}
        label={t("assign")}
        trigger={
          assigned ? (
            <button type="button" className="cursor-pointer">{chip}</button>
          ) : (
            <Button type="button" variant="outline" size="sm" className="h-7 gap-1 px-2 text-[11px]">
              <IconCar className="size-3.5" />
              {t("assign")}
            </Button>
          )
        }
      />
    </span>
  )
}
